/**
 * Free-text multi-value input rendered as chips, with optional one-click
 * suggestions. Enter / comma / blur commits; Backspace on an empty box removes
 * the last chip. Used for demo targets, Culture Stacks, dayparts, conquesting
 * and aliases.
 */

import React, { useId, useState } from 'react';
import { fieldStyle, hintStyle, labelStyle } from './formStyles';

interface Props {
  label: string;
  values: string[];
  onChange: (next: string[]) => void;
  suggestions?: readonly string[];
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  maxSuggestions?: number;
}

const ChipInput: React.FC<Props> = ({ label, values, onChange, suggestions = [], placeholder, hint, disabled, maxSuggestions = 12 }) => {
  const id = useId();
  const [text, setText] = useState('');

  const add = (raw: string) => {
    const parts = raw.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    const next = [...values];
    for (const p of parts) if (!next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p);
    onChange(next);
    setText('');
  };
  const remove = (value: string) => onChange(values.filter((v) => v !== value));
  const available = suggestions.filter((s) => !values.some((v) => v.toLowerCase() === s.toLowerCase())).slice(0, maxSuggestions);

  return (
    <div>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      {values.length > 0 && (
        <div style={styles.chipRow}>
          {values.map((v) => (
            <span key={v} style={styles.chip}>
              {v}
              <button type="button" aria-label={`Remove ${v}`} onClick={() => remove(v)} style={styles.chipX} disabled={disabled}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <input
        id={id}
        style={fieldStyle}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add(text);
          } else if (e.key === 'Backspace' && !text && values.length > 0) {
            remove(values[values.length - 1]);
          }
        }}
        onBlur={() => {
          if (text.trim()) add(text);
        }}
      />
      {available.length > 0 && (
        <div style={styles.suggestRow} aria-label={`${label} suggestions`}>
          {available.map((s) => (
            <button type="button" key={s} style={styles.suggestChip} onClick={() => add(s)} disabled={disabled}>
              + {s}
            </button>
          ))}
        </div>
      )}
      {hint && <div style={hintStyle}>{hint}</div>}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px 4px 10px',
    borderRadius: 'var(--radius-pill)',
    background: 'var(--accent-soft)',
    color: 'var(--accent-soft-fg)',
    fontSize: 12.5,
    fontWeight: 600,
    fontFamily: 'var(--font-sans)',
  },
  chipX: {
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
    padding: '0 2px',
  },
  suggestRow: { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 },
  suggestChip: {
    padding: '3px 8px',
    borderRadius: 'var(--radius-pill)',
    border: '1px dashed var(--border-2)',
    background: 'transparent',
    color: 'var(--fg-3)',
    fontSize: 11.5,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
};

export default ChipInput;
